﻿import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DT, DS2 } from '../../theme/designTokens';
import { NAV_ITEMS, SIDEBAR_NAV_ITEMS } from '../../config/navigation';
import { ASSETS } from '../../config/assets';
import type { NavItem } from '../../config/navigation';
import { useLogout } from '../../hooks/useLogout';
import { useFriends } from '../../context/FriendContext';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/react';
import { NAV_ICON_ASSET, AchievementsNavIcon } from './Sidebar';
import { LanguageSelector } from '../ui/LanguageSelector';
import { UnreadBadge } from '../ui/UnreadBadge';

interface Props {
  open:    boolean;
  onClose: () => void;
  /**
   * DS2 mobile drawer for routes on AppShell's SIDEBAR_SHELL_ROUTES bridge:
   * same nav set, icons, colors, and auth actions as the desktop Sidebar.
   * Legacy routes keep the original drawer unchanged.
   */
  ds2?: boolean;
}

function SoonBadge() {
  // Compact 9px badge — es-419 uses the approved short form "Pronto"
  // ("Próximamente" is verified not to fit this badge's constraints).
  const { t } = useTranslation('navigation');
  return (
    <span
      style={{
        fontSize:      9,
        fontWeight:    700,
        fontFamily:    'Barlow, sans-serif',
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        color:         DT.color.textSecondary,
        background:    'rgba(255,255,255,0.08)',
        borderRadius:  4,
        padding:       '2px 5px',
        lineHeight:    1,
        marginLeft:    'auto',
      }}
    >
      {t('badge.soon')}
    </span>
  );
}

function DrawerNavItem({ item, active, onClose, badge, ds2 }: { item: NavItem; active: boolean; onClose: () => void; badge?: number; ds2?: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation('navigation');
  const handleClick = () => { onClose(); navigate(item.path); };
  const iconSrc = ds2 ? NAV_ICON_ASSET[item.icon] : undefined;
  return (
    <button
      onClick={handleClick}
      data-tutorial-target={`nav:${item.path}`}
      style={{
        display:    'flex',
        alignItems: 'center',
        gap:        ds2 ? 10 : 14,
        width:      '100%',
        padding:    ds2 ? '8px 24px' : '14px 24px',
        background:  !ds2 && active ? 'rgba(201,168,76,0.08)' : 'transparent',
        border:      'none',
        borderLeft:  ds2 ? 'none' : (active ? `3px solid ${DT.color.gold}` : '3px solid transparent'),
        // DS2: same states as the desktop Sidebar — active #FFC844, rest bright white
        color:       ds2 ? (active ? '#FFC844' : DS2.color.textBright)
                         : (active ? DT.color.gold : DT.color.textSecondary),
        cursor:      'pointer',
        textAlign:   'left',
        WebkitTapHighlightColor: 'transparent',
        fontSize:    ds2 ? 16 : DT.type.navItem.size,
        fontWeight:  ds2 ? 700 : (active ? 600 : 500),
        fontFamily:  ds2 ? DS2.font.heading : DT.font.sans,
      }}
    >
      {item.icon === 'achievements' ? (
        <AchievementsNavIcon size={34} color={ds2 ? (active ? '#FFC844' : DS2.color.textBright) : DT.color.gold} />
      ) : iconSrc && (
        <img src={iconSrc} alt="" style={{ width: 34, height: 34, flexShrink: 0, objectFit: 'contain' }} draggable={false} />
      )}
      <span style={{ flex: 1 }}>{t(`items.${item.labelKey}`)}</span>
      {(badge != null && badge > 0) && (
        <span style={{
          backgroundColor: DT.color.gold, color: DT.color.goldOnGold,
          fontSize: 10, fontWeight: 800, fontFamily: DT.font.sans,
          minWidth: 16, height: 16, borderRadius: 999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 4px', lineHeight: 1,
        }}>
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      {item.soon && <SoonBadge />}
    </button>
  );
}

export function Drawer({ open, onClose, ds2 = false }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('navigation');
  const { t: tc } = useTranslation('common');
  const { t: tm } = useTranslation('messages');
  // Shared logout behavior — identical across every active logout control.
  const { loggingOut, logout } = useLogout(() => navigate('/'));
  const { pendingCount, unreadMessageCount = 0 } = useFriends();
  const { isLoggedIn } = useAuth();
  const isActive = (p: string) => pathname === p || pathname.startsWith(p + '/');
  // Docked Chat Redesign: the logged-in DS2 drawer mirrors the desktop
  // sidebar — the plain Messages nav row is replaced by the dedicated CHAT
  // entry rendered with the bottom actions below (same gold pill concept).
  // Logged-out keeps the plain nav row, exactly like the desktop sidebar.
  const showChatEntry = ds2 && isLoggedIn;
  const navItems = ds2
    ? (showChatEntry ? SIDEBAR_NAV_ITEMS.filter(i => i.icon !== 'messages') : SIDEBAR_NAV_ITEMS)
    : NAV_ITEMS;

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Close the drawer immediately (as before), then run the shared logout.
  // The single-flight guard + finally self-heal live in useLogout.
  function handleLogOut() {
    onClose();
    logout();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position:      'fixed',
          inset:         0,
          zIndex:        40,
          background:    'rgba(0,0,0,0.6)',
          opacity:       open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition:    `opacity ${DT.motion.normal}ms ${DT.motion.easing}`,
        }}
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('aria.navMenu')}
        style={{
          position:      'fixed',
          top:           0,
          left:          0,
          bottom:        0,
          zIndex:        41,
          width:         280,
          background:    ds2 ? DS2.color.surface : DT.color.sidebarBg,
          borderRight:   ds2 ? '1px solid #CB9719' : `1px solid ${DT.color.border}`,
          display:       'flex',
          flexDirection: 'column',
          transform:     open ? 'translateX(0)' : 'translateX(-100%)',
          transition:    `transform ${DT.motion.normal}ms ${DT.motion.easing}`,
          overflowY:     'auto',
        }}
      >
        {/* Header — top padding absorbs the status-bar/notch inset on native
            (fixed full-height panel starts at the true screen top). */}
        <div
          style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            padding:        `calc(14px + ${DT.layout.safeAreaTop}) 24px 14px`,
            borderBottom:   `1px solid ${DT.color.border}`,
            flexShrink:     0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img
              src={ds2 ? ASSETS.brand.LOGO_SIDEBAR : ASSETS.brand.LOGO_HORIZONTAL}
              alt="The Domino Game"
              style={{ height: ds2 ? 44 : 36, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
              draggable={false}
            />
          </div>
          <button
            onClick={onClose}
            aria-label={t('aria.closeNav')}
            style={{
              width:          36,
              height:         36,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              background:     'transparent',
              border:         `1px solid ${DT.color.border}`,
              borderRadius:   DT.radius.sm,
              cursor:         'pointer',
              color:          DT.color.textSecondary,
              flexShrink:     0,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Primary nav — DS2 routes use the Sidebar's nav set, legacy the deployed order */}
        <nav style={{ flex: 1, paddingTop: 8 }}>
          {navItems.map(item => (
            <DrawerNavItem
              key={item.path}
              item={item}
              active={isActive(item.path)}
              onClose={onClose}
              badge={item.path === '/friends' ? pendingCount : undefined}
              ds2={ds2}
            />
          ))}
        </nav>

        {ds2 ? (
          /* DS2 bottom — same auth actions as the desktop Sidebar */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: `16px 24px calc(20px + ${DT.layout.safeAreaBottom})`, flexShrink: 0 }}>
            {/* CHAT entry — the mobile counterpart of the desktop sidebar's
                CHAT button: same gold pill concept + total unread badge.
                On mobile it opens the full-screen conversation list. */}
            {showChatEntry && (
              <button
                onClick={() => { onClose(); navigate('/messages'); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  width: 155, minHeight: 36, padding: '8px 16px',
                  borderRadius: DS2.radius.button, background: '#131313',
                  border: '1px solid rgba(201,168,76,0.45)',
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span style={{
                  fontFamily: DS2.font.heading, fontSize: 12, fontWeight: 700,
                  letterSpacing: 1.4, textTransform: 'uppercase', color: DS2.color.gold,
                }}>
                  {tm('chat.title')}
                </span>
                {unreadMessageCount > 0 && (
                  <span aria-label={tm('chat.unreadAria', { count: unreadMessageCount })}>
                    <UnreadBadge count={unreadMessageCount} />
                  </span>
                )}
              </button>
            )}
            {/* Docked Chat Redesign: mirrors the desktop Sidebar exactly —
                logged-in mobile carries no Log Out (it lives on Settings)
                and no language selector (Settings owns that too once
                logged in); logged-out mobile keeps Sign Up/Log In plus the
                same compact language selector as the logged-out desktop
                sidebar. */}
            {!isLoggedIn && (
              <>
                <button
                  onClick={() => { onClose(); navigate('/signup'); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 155, minHeight: 36, padding: '8px 16px',
                    borderRadius: DS2.radius.button, background: DS2.gradient.gold,
                    border: 'none', color: '#FFFFFF',
                    fontSize: 14, fontWeight: 500, fontFamily: DS2.font.body,
                    boxShadow: DT.shadow.gold, cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {tc('action.signUp')}
                </button>
                <button
                  onClick={() => { onClose(); navigate('/login'); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 155, minHeight: 36, padding: '8px 16px',
                    borderRadius: DS2.radius.button, background: '#131313',
                    border: `1px solid ${DS2.color.gold}`, color: '#FFFFFF',
                    fontSize: 14, fontWeight: 500, fontFamily: DS2.font.body,
                    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {tc('action.logIn')}
                </button>
                {/* Pre-login language selector — opens upward (this block
                    sits at the panel's bottom edge). Does NOT close the
                    drawer; it re-renders in place with the new labels. */}
                <div style={{ width: 155 }}>
                  <LanguageSelector variant="compact" openUp id="drawer-language" />
                </div>
              </>
            )}
            <button
              onClick={() => { onClose(); navigate('/help'); }}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 500, fontFamily: DS2.font.body,
                color: DS2.color.textBright, textDecoration: 'underline',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Help &amp; Support
            </button>
          </div>
        ) : (
          /* Legacy bottom: log out only — Settings now lives in NAV_ITEMS
             above (one canonical /settings identity, Settings V1). */
          <div style={{ borderTop: `1px solid ${DT.color.border}`, paddingTop: 4, paddingBottom: `calc(12px + ${DT.layout.safeAreaBottom})`, flexShrink: 0 }}>
            <button
              onClick={handleLogOut}
              disabled={loggingOut}
              style={{
                display:    'flex',
                alignItems: 'center',
                gap:        14,
                width:      '100%',
                padding:    '14px 24px',
                background: 'transparent',
                border:     'none',
                borderLeft: '3px solid transparent',
                color:      DT.color.textSecondary,
                cursor:     loggingOut ? 'default' : 'pointer',
                textAlign:  'left',
                fontSize:   DT.type.navItem.size,
                fontWeight: 500,
                fontFamily: DT.font.sans,
                opacity:    loggingOut ? 0.5 : 1,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {/* legacy.logOut preserves this drawer's exact historical
                  "Log out" casing (distinct from DS2's "Log Out"). */}
              {loggingOut ? tc('status.signingOut') : t('legacy.logOut')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
